import { CSSProperties, ReactNode, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { isTopmostOverlay } from './overlayStack'
import { POPOVER_Z_INDEX } from './z-index'

export interface PopoverProps {
    trigger: ReactNode
    title?: string
    /** Accessible name of the panel when it has no visible `title`. */
    ariaLabel?: string
    children: ReactNode
    style?: CSSProperties
    /** Controlled open state; leave both `open` and `onOpenChange` out for an uncontrolled popover. */
    open?: boolean
    onOpenChange?: (open: boolean) => void
    /** Where the panel sits under the trigger: centred (default) or flush with its left edge. */
    align?: 'center' | 'start'
    /** Beside the title, e.g. a "Clear" link. */
    action?: ReactNode
    /** Replaces the panel's default width and padding, for panels that need their own. */
    panelClassName?: string
    /**
     * Render the panel in a portal on `document.body`, positioned from the trigger and flipped above it when there is no room below.
     * For triggers inside a container that clips overflow (table cells, cards), where the default un-portaled panel would be cut off.
     */
    portal?: boolean
    /** Stretch the trigger to the width of its container, for a trigger that fills a table cell. Off by default: the trigger hugs its content. */
    fullWidth?: boolean
    /** Stretch the trigger to the height of its container too, for a trigger that fills a table cell top to bottom. */
    fullHeight?: boolean
    /** Stacking order of a portaled panel. Defaults to `POPOVER_Z_INDEX`; lower it only to tuck the panel under a dialog it raised. */
    zIndex?: number
    /** For a panel that holds a Select: its options open in a portal of their own, and picking one is not a click outside. Off by default. */
    keepOpenOnListboxClick?: boolean
}

const VIEWPORT_MARGIN = 8
const PANEL_GAP = 8

const DEFAULT_PANEL = 'min-w-[200px] gap-2 rounded-3xl px-6 py-4'

export const Popover = ({
    trigger,
    title,
    ariaLabel,
    children,
    style,
    open: controlledOpen,
    onOpenChange,
    align = 'center',
    action,
    panelClassName = DEFAULT_PANEL,
    portal = false,
    fullWidth = false,
    fullHeight = false,
    zIndex = POPOVER_Z_INDEX,
    keepOpenOnListboxClick = false,
}: PopoverProps) => {
    const [internalOpen, setInternalOpen] = useState(false)
    const open = controlledOpen ?? internalOpen
    const containerRef = useRef<HTMLDivElement>(null)
    const panelRef = useRef<HTMLDivElement>(null)
    const [position, setPosition] = useState<{ top: number; left: number } | null>(null)

    const setOpen = (next: boolean) => {
        setInternalOpen(next)
        onOpenChange?.(next)
    }
    const setOpenRef = useRef(setOpen)
    setOpenRef.current = setOpen

    useEffect(() => {
        if (!open) return
        const onMouseDown = (e: MouseEvent) => {
            const target = e.target as Node
            if (containerRef.current?.contains(target) || panelRef.current?.contains(target)) return
            if (keepOpenOnListboxClick && target instanceof Element && target.closest('[role="listbox"]')) return
            setOpenRef.current(false)
        }
        const onKeyDown = (e: KeyboardEvent) => {
            // Only the topmost overlay answers Escape: a popover inside a Modal must close alone, not take the Modal with it.
            if (e.key === 'Escape' && isTopmostOverlay(panelRef.current)) setOpenRef.current(false)
        }
        // Capture phase: a click outside still closes the panel when the element under it stops mousedown from bubbling.
        document.addEventListener('mousedown', onMouseDown, true)
        document.addEventListener('keydown', onKeyDown)
        return () => {
            document.removeEventListener('mousedown', onMouseDown, true)
            document.removeEventListener('keydown', onKeyDown)
        }
    }, [open, keepOpenOnListboxClick])

    // Focus moves into the panel when it opens (unless something inside, like a search field, took it already) and goes back to
    // where it was when it closes, so keyboard and screen-reader users are not left behind the panel.
    const returnFocusTo = useRef<HTMLElement | null>(null)
    useEffect(() => {
        if (!open) return
        returnFocusTo.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
        const frame = requestAnimationFrame(() => {
            const panelEl = panelRef.current
            if (panelEl && !panelEl.contains(document.activeElement)) panelEl.focus({ preventScroll: true })
        })
        return () => {
            cancelAnimationFrame(frame)
            const target = returnFocusTo.current
            const active = document.activeElement
            // By now the panel is gone, so focus that was inside it has fallen back to the page: put it back on what opened the panel.
            if (target && document.contains(target) && (!active || active === document.body)) target.focus({ preventScroll: true })
        }
    }, [open])

    useLayoutEffect(() => {
        if (!portal || !open) {
            setPosition(null)
            return
        }
        const place = () => {
            const anchor = containerRef.current?.getBoundingClientRect()
            const panel = panelRef.current
            if (!anchor || !panel) return
            const fitsBelow = anchor.bottom + PANEL_GAP + panel.offsetHeight <= window.innerHeight - VIEWPORT_MARGIN
            const top = fitsBelow ? anchor.bottom + PANEL_GAP : Math.max(VIEWPORT_MARGIN, anchor.top - PANEL_GAP - panel.offsetHeight)
            const preferred = align === 'start' ? anchor.left : anchor.left + anchor.width / 2 - panel.offsetWidth / 2
            const left = Math.max(VIEWPORT_MARGIN, Math.min(preferred, window.innerWidth - panel.offsetWidth - VIEWPORT_MARGIN))
            setPosition({ top, left })
        }
        place()
        const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(place)
        if (panelRef.current) observer?.observe(panelRef.current)
        window.addEventListener('resize', place)
        window.addEventListener('scroll', place, true)
        return () => {
            observer?.disconnect()
            window.removeEventListener('resize', place)
            window.removeEventListener('scroll', place, true)
        }
    }, [portal, open, align])

    const panel = open && (
        <div
            ref={panelRef}
            role="dialog"
            data-ds2-overlay
            aria-label={title ?? ariaLabel}
            tabIndex={-1}
            onClick={portal ? (event) => event.stopPropagation() : undefined}
            style={
                portal
                    ? {
                          position: 'fixed',
                          top: position?.top ?? 0,
                          left: position?.left ?? 0,
                          zIndex,
                          visibility: position ? 'visible' : 'hidden',
                      }
                    : undefined
            }
            className={`flex flex-col outline-none bg-surface shadow-overlay backdrop-blur-[20px] ${
                portal ? '' : `absolute top-full z-50 mt-2 ${align === 'start' ? 'left-0' : 'left-1/2 -translate-x-1/2'}`
            } ${panelClassName}`}
        >
            {(title || action) && (
                <div className="flex items-center justify-between gap-2">
                    {title && <p className="m-0 text-sm font-medium leading-5 text-foreground">{title}</p>}
                    {action}
                </div>
            )}
            <div className="text-sm font-normal text-foreground-muted">{children}</div>
        </div>
    )

    return (
        <div
            ref={containerRef}
            className={`relative flex-col items-center gap-2 ${fullWidth ? 'flex w-full' : 'inline-flex'} ${fullHeight ? 'h-full' : ''}`}
            style={style}
        >
            <div
                onClick={() => setOpen(!open)}
                className={`cursor-pointer ${fullWidth ? 'w-full' : ''} ${fullHeight ? 'h-full' : ''}`}
                aria-haspopup="dialog"
                aria-expanded={open}
            >
                {trigger}
            </div>

            {portal && panel ? createPortal(panel, document.body) : panel}
        </div>
    )
}
